import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";

import { AnnotationIcon, CameraIcon } from "../../icons/glyphs";
import { useAnnotationTargetShortcuts } from "../AnnotationTargetStrip";
import { IconButton } from "../IconButton";
import { formatMediaPlaybackTime, useMediaTimelineShortcuts } from "../MediaTimelineBar";
import { Spinner } from "../Spinner";
import { ToastHost, ToastManager } from "../Toast";
import type { PlaybackTransport, VideoStatus } from "../Video";
import { isRenderableNode } from "../../utils/reactNode";

import {
  AnnotationControlsPlaceholder,
  AnnotationInspectorBody,
  InlineFramePreview,
  AnnotationInspectorRoot,
  AnnotationToolsOverlay,
  AnnotationInspectorTools,
  AnnotationPanelBody,
  AnnotationPanelLeadSection,
  AnnotationPanelRoot as AnnotationPanel,
} from "./AnnotationPanelLayout";
import { AnnotationPanelHeaderContent } from "./AnnotationPanelHeaderContent";
import {
  AnnotationPointActions,
  EMPTY_ANNOTATION_COORDINATE_OVERLAY,
} from "./AnnotationPointActions";
import { AnnotationToolsPanel } from "./AnnotationToolsPanel";
import { useAnnotationToolsPresentation } from "./useAnnotationToolsPresentation";
import { CapturedFramePanel, type CapturedFrameScope } from "./CapturedFramePanel";
import { CapturedFramePreviewPanel } from "./CapturedFramePreviewPanel";
import { useCapturedFramePreviewLayout } from "./useCapturedFramePreviewLayout";
import { useCapturedFramePreviewSelection } from "./useCapturedFramePreviewSelection";
import { CapturedFramePreviewPopup } from "./CapturedFramePreviewPopup";
import { AnnotationValidationReviewModal } from "./AnnotationValidationReviewModal";
import { StudioCommandBar } from "./StudioCommandBar";
import { StudioContextRail, StudioWorkspace } from "./StudioContextRail";
import { StudioDialogs } from "./StudioDialogs";
import { StudioAuthoringAvailabilityMessage, StudioMediaControls } from "./StudioMediaControls";
import { StudioViewport, type StudioMediaContext } from "./StudioViewport";
import { TrackingPreviewControls, useTrackingPreviewPlayback } from "./TrackingPreview";
import {
  annotationStudioText,
  captureTimestampAdjustedMessage,
  resolveAnnotationOperationAvailabilityReason,
  resolveAnnotationOperationErrorMessage,
} from "./messages";
import {
  MediaAnnotationStudioMain as Main,
  MediaAnnotationStudioRoot as StudioRoot,
  MediaAnnotationWorkspace as MediaWorkspace,
  MediaAnnotationWorkspaceBody as WorkspaceBody,
} from "./MediaAnnotationStudioLayout";
import { targetColorCss } from "./targetColor";
import { persistedTrackingConditioningStreamIds } from "./trackingConditioning";
import type {
  AnnotationMediaResultBinding,
  AnnotationMediaSource,
  AnnotationPointKind,
  AnnotationStudioSequenceActionRenderer,
  AnnotationStudioController,
  AnnotationStudioExitRequestResult,
  AnnotationStudioExitResult,
  AnnotationStudioValidationReviewResult,
  AnnotationStudioViewMode,
  TrackingPreviewTargetMedia,
} from "./types";
import { useAnnotationStudioSnapshot } from "./useAnnotationStudioController";
import { useAnnotationSetPresentation } from "./useAnnotationSetPresentation";
import { useAnnotationSegmentation } from "./useAnnotationSegmentation";
import { useAnnotationValidationGuidanceToast } from "./useAnnotationValidationGuidanceToast";
import { useAnnotationOperationErrorToast } from "./useAnnotationOperationErrorToast";
import {
  isAnnotationStudioTextEditingTarget,
  useAnnotationStudioShortcuts,
} from "./useAnnotationStudioShortcuts";
import { useCapturedFrameActions } from "./useCapturedFrameActions";
import { useCapturedFrameCollection } from "./useCapturedFrameCollection";
import { useStableHandler } from "./useStableHandler";
import { useCapturedFramePresentation } from "./useCapturedFramePresentation";
import { useCapturedFrameRenderResource } from "./useCapturedFrameRenderResource";
import { useStudioNavigationGuards } from "./useStudioNavigationGuards";
import { useStudioPlayback } from "./useStudioPlayback";
import { useTargetPresentation } from "./useTargetPresentation";
import { useStudioErrorToast } from "./useStudioErrorToast";
import { useTrackingPreviewErrorToast } from "./useTrackingPreviewErrorToast";
import { resolveTrackingPreviewAvailability } from "./useTrackingPreviewAvailability";

const STUDIO_TIMELINE_SHORTCUT_COMMANDS = [
  "toggle",
  "step",
  "createMarker",
  "removeMarker",
] as const;
const EMPTY_NEW_SEQUENCE_IDS: readonly string[] = [];

// Capture intents are authored on the timeline's 0.1-second grid. A larger correction is a
// user-visible relocation rather than ordinary source-frame resolution jitter.
const CAPTURE_TIMESTAMP_NOTICE_THRESHOLD_SECONDS = 0.1;

export type {
  AnnotationStudioSequenceActionContext,
  AnnotationStudioSequenceActionMode,
  AnnotationStudioSequenceActionPresentation,
  AnnotationStudioSequenceActionRenderer,
  AnnotationStudioSequenceActionResult,
} from "./types";

function formatMediaContext(
  sequenceLabel: string | null | undefined,
  streamLabel: string | null | undefined,
): string {
  return (
    [sequenceLabel, streamLabel].filter((label): label is string => Boolean(label)).join(" · ") ||
    "—"
  );
}

function resolveStudioMediaSource(
  controller: AnnotationStudioController,
  sequenceId: string | null,
  streamId: string | null,
): AnnotationMediaSource | null {
  if (sequenceId === null || streamId === null) return null;
  if (controller.resolveMediaSource) return controller.resolveMediaSource(sequenceId, streamId);
  const workspace = controller.getSnapshot().workspace;
  const sequence = workspace.sequences.find((candidate) => candidate.id === sequenceId);
  const stream = workspace.streams.find((candidate) => candidate.id === streamId);
  return sequence?.streamIds.includes(streamId) && stream ? (stream.media ?? null) : null;
}

/**
 * 제품이 노드를 심는 자리 묶음. c6 는 자리를 배치하고 그 안의 내용·상태·게이팅은 제품이 소유한다.
 * 주지 않은 자리는 렌더하지 않는다 — 빈 상자도 레이아웃 시프트도 없다.
 *
 * 자리를 한 묶음으로 모으는 이유는 "제품이 무엇을 심을 수 있는가"가 나머지 prop 사이에 흩어지지
 * 않게 하려는 것이다. 문자열 축을 `labels` 한 묶음이 갖는 것과 같은 자리에 노드 축이 선다.
 *
 * 제품 도메인 개념은 이 자리를 통해서도 c6 계약에 들어오지 않는다 — c6 는 노드를 배치만 한다.
 */
export interface MediaAnnotationStudioSlots {
  /**
   * Timeline's consumer action seat. Omitting it leaves c6's own tracking control in the seat;
   * passing an explicit `null` empties the seat instead.
   */
  timelineAction?: ReactNode;
  /**
   * Per-sequence action seat, called only for rendered rows — a renderer rather than a node
   * because each row gets its own. Return presentation metadata to distinguish available
   * start/replay actions from persistent stop/retry recovery.
   */
  sequenceAction?: AnnotationStudioSequenceActionRenderer;
  /**
   * 카메라·에피소드 앞에 제품이 렌더한 노드를 놓는 범용 자리.
   * 제목·컨트롤·상태·액션은 소비처가 소유하며 c6는 노드의 배치만 담당한다.
   * 좁은 화면에서는 탐색 목록 위 첫 행 전체를 사용한다. 긴 내용은 내부 스크롤로 제한하며,
   * 렌더할 내용이 없으면 자리와 여백을 만들지 않는다.
   */
  contextLead?: ReactNode;
  /**
   * Section planted at the head of the annotation panel, above the annotation tools and
   * captured-frame sections.
   *
   * The seat is capped at a share of the panel body and scrolls beyond it, and it yields its
   * height before the sections below do — so a tall product node never pushes the annotation
   * tools or captured frames past their own floors. Keep it short.
   *
   * A node that renders nothing (`false` from `cond && <Node />`, `null`, an empty string) leaves
   * no seat at all — no empty band, no divider, no reserved height.
   */
  panelLead?: ReactNode;
  /**
   * Layer covering the annotation tools section. c6 places it and draws the scrim; the product
   * owns the content and decides when to supply it.
   *
   * Use it for a state that makes the tools unusable as a whole — a read-only annotation set, a
   * permission block — where a disabled control alone does not explain itself. The captured-frame
   * section stays uncovered so a locked set can still be reviewed. Give the content its own opaque
   * surface: the scrim sits over arbitrary panel content, so text cannot rely on it for contrast.
   *
   * A node that renders nothing (`false` from `cond && <Node />`, `null`, an empty string) leaves
   * no layer at all — the tools stay unscrimmed and fully clickable.
   */
  toolsOverlay?: ReactNode;
}

export interface MediaAnnotationStudioProps {
  controller: AnnotationStudioController;
  className?: string;
  /**
   * Product-owned transient sequence ids rendered as localized New badges. Unknown ids are
   * ignored, and c6 does not persist or clear this workflow hint.
   */
  newSequenceIds?: readonly string[];
  /** Product-owned completed/live result. c6 synchronizes and renders it without executing jobs. */
  mediaResult?: AnnotationMediaResultBinding | null;
  /** Product-owned seats. See `MediaAnnotationStudioSlots`. */
  slots?: MediaAnnotationStudioSlots;
  /** Optional product navigation boundary. The Studio saves dirty frame annotations before calling it. */
  onExit?: (result: AnnotationStudioExitResult) => void | Promise<void>;
  /**
   * Product-owned exit action request token. Changing it runs the same save/validation preflight
   * as the built-in exit action and suppresses that duplicate action from the command bar.
   */
  exitRequestId?: number;
  /** Settles a product-owned exit request after save/leave succeeds or the request is cancelled. */
  onExitRequestSettled?: (result: AnnotationStudioExitRequestResult) => void;
  /**
   * Product-owned navigation review token. Changing it validates the current set without saving
   * or exiting, then settles the request through `onValidationReviewSettled`.
   */
  validationReviewRequestId?: number;
  /** Receives the matching request result; c6 never saves or exits through this review seam. */
  onValidationReviewSettled?: (result: AnnotationStudioValidationReviewResult) => void;
}

export function MediaAnnotationStudio({
  controller,
  className,
  newSequenceIds = EMPTY_NEW_SEQUENCE_IDS,
  mediaResult = null,
  slots,
  onExit,
  exitRequestId,
  onExitRequestSettled,
  validationReviewRequestId,
  onValidationReviewSettled,
}: MediaAnnotationStudioProps) {
  // 자리는 묶음으로 받고 여기서 한 번 편다. 아래 코드는 자리 하나씩만 본다 — 묶음은 public
  // 경계의 모양이지 내부로 전파하는 값이 아니다.
  // 자리를 주지 않은 것(`undefined`)과 비운 것(`null`)은 다르게 읽힌다: timeline 은 전자에서만
  // c6 자체 컨트롤로 되돌아가고, sequence 는 전자에서만 c6 가 자기 열을 접는다.
  const { timelineAction, sequenceAction, contextLead, panelLead, toolsOverlay } = slots ?? {};
  // 자리를 세우는 기준은 "값을 줬는가"가 아니라 **렌더하면 무언가 그리는가**다. `cond && <Node/>`
  // 는 조건이 거짓일 때 `false` 를 남기는데, 그 값으로 자리를 세우면 내용 없는 껍데기가 남는다 —
  // 오버레이는 설명 없는 scrim 이 되어 아래 도구의 포인터를 먹고, 선두 자리는 빈 띠와 구분선으로
  // 아래 섹션의 세로를 먹는다. timeline·sequence 자리가 이미 같은 헬퍼로 판단한다.
  const panelLeadVisible = isRenderableNode(panelLead);
  const toolsOverlayVisible = isRenderableNode(toolsOverlay);
  const snapshot = useAnnotationStudioSnapshot(controller);
  const { workspace, locale } = snapshot;
  const sequenceDirectory = snapshot.sequenceDirectory ?? null;
  const railSequences = useMemo(() => {
    if (!sequenceDirectory) return workspace.sequences;
    const items = [...sequenceDirectory.items];
    if (
      snapshot.pinnedSequence &&
      !items.some((sequence) => sequence.id === snapshot.pinnedSequence?.id)
    ) {
      items.unshift(snapshot.pinnedSequence);
    }
    return items;
  }, [sequenceDirectory, snapshot.pinnedSequence, workspace.sequences]);
  const newSequenceIdSet = useMemo(() => new Set(newSequenceIds), [newSequenceIds]);
  const authoring = snapshot.mode === "authoring";
  const authoringOperationsAvailable =
    authoring && snapshot.authoringAvailability?.allowed !== false;
  const segmentationAvailable =
    snapshot.segmentationAvailability?.allowed ??
    (authoring ? authoringOperationsAvailable : false);
  const authoringAvailabilityReason =
    authoring && snapshot.authoringAvailability?.allowed === false
      ? resolveAnnotationOperationAvailabilityReason(snapshot.authoringAvailability, locale)
      : undefined;
  const authoringAvailabilityReasonId = useId();
  const [coordinateOverlay, setCoordinateOverlay] = useState(EMPTY_ANNOTATION_COORDINATE_OVERLAY);
  const [clearAllConfirmationOpen, setClearAllConfirmationOpen] = useState(false);
  const [discardChangesConfirmationOpen, setDiscardChangesConfirmationOpen] = useState(false);
  const [frameRemovalConfirmationId, setFrameRemovalConfirmationId] = useState<string | null>(null);
  const [capturedFrameScope, setCapturedFrameScope] = useState<CapturedFrameScope>("all-camera");
  const [validationPopupOpenRequestId, setValidationPopupOpenRequestId] = useState(0);
  const [studioToastManager] = useState(() => new ToastManager());
  const studioRootRef = useRef<HTMLDivElement>(null);
  const frameCaptureButtonRef = useRef<HTMLButtonElement>(null);
  const {
    presentation: currentTargetPresentation,
    setSelectedOnly,
    setVisibleTargets,
    setTargetVisibility,
    setTargetName,
    selectTarget,
  } = useTargetPresentation({
    collectionId: workspace.collectionId,
    targets: workspace.targets,
    selectedTargetId: snapshot.selectedTargetId,
    onSelectTarget: controller.selectTarget,
  });
  const visibleTargetIds = currentTargetPresentation.visibleTargetIds;
  const sequence = railSequences.find((candidate) => candidate.id === snapshot.selectedSequenceId);
  const stream = workspace.streams.find((candidate) => candidate.id === snapshot.selectedStreamId);
  const selectedMedia = resolveStudioMediaSource(
    controller,
    snapshot.selectedSequenceId,
    snapshot.selectedStreamId,
  );
  const selectedTrackingTargetMedia: TrackingPreviewTargetMedia | null =
    snapshot.selectedSequenceId !== null && snapshot.selectedStreamId !== null
      ? {
          sequenceId: snapshot.selectedSequenceId,
          streamId: snapshot.selectedStreamId,
        }
      : null;
  const {
    store: playbackStore,
    transport: playbackTransport,
    requestCapturedFrameSeek,
    cancelCapturedFrameSeek,
  } = useStudioPlayback({
    streamId: stream?.id,
    selectedSequenceId: snapshot.selectedSequenceId,
    selectedStreamId: snapshot.selectedStreamId,
    durationSeconds: sequence?.durationSeconds ?? selectedMedia?.durationSeconds,
  });
  const pendingInteractionTaskRef = useRef<{
    animationFrameId: number | null;
    timeoutId: number | null;
  }>({ animationFrameId: null, timeoutId: null });
  const cancelPendingInteractionTask = useCallback(() => {
    const pending = pendingInteractionTaskRef.current;
    if (typeof window !== "undefined") {
      if (pending.animationFrameId !== null) {
        window.cancelAnimationFrame(pending.animationFrameId);
      }
      if (pending.timeoutId !== null) window.clearTimeout(pending.timeoutId);
    }
    pending.animationFrameId = null;
    pending.timeoutId = null;
  }, []);
  const scheduleAfterInteractionPaint = useCallback(
    (task: () => void) => {
      cancelPendingInteractionTask();
      const run = () => {
        pendingInteractionTaskRef.current.timeoutId = null;
        task();
      };
      if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
        run();
        return;
      }
      pendingInteractionTaskRef.current.animationFrameId = window.requestAnimationFrame(() => {
        pendingInteractionTaskRef.current.animationFrameId = null;
        pendingInteractionTaskRef.current.timeoutId = window.setTimeout(run, 0);
      });
    },
    [cancelPendingInteractionTask],
  );
  useEffect(() => cancelPendingInteractionTask, [cancelPendingInteractionTask]);
  useEffect(() => {
    cancelPendingInteractionTask();
  }, [cancelPendingInteractionTask, snapshot.selectedSequenceId, snapshot.selectedStreamId]);
  const target = workspace.targets.find((candidate) => candidate.id === snapshot.selectedTargetId);
  const selectedTargetColor = target ? targetColorCss(target.color) : "currentColor";
  const frame =
    snapshot.draft !== null && snapshot.draft.activeFrameId !== null
      ? snapshot.draft.frames[snapshot.draft.activeFrameId]
      : undefined;
  const mediaContext: StudioMediaContext =
    snapshot.viewMode === "annotate" && frame
      ? { kind: "captured-frame", frameId: frame.frameId }
      : { kind: "video" };
  // `viewMode` remains the public controller compatibility surface. Presentation code uses the
  // explicit media context so capture stays an action on video rather than a peer UI mode.
  const studioViewMode: AnnotationStudioViewMode =
    mediaContext.kind === "captured-frame" ? "annotate" : "browse";
  // A product result is a browse presentation, not a second Studio mode. Opening a captured frame
  // for Annotation temporarily yields the viewport and shared timeline to that edit context.
  const activeMediaResult = studioViewMode === "browse" ? mediaResult : null;
  const trackingTargetMedia = snapshot.trackingPreview.targetMedia;
  const trackingTargetStream = trackingTargetMedia
    ? workspace.streams.find((candidate) => candidate.id === trackingTargetMedia.streamId)
    : undefined;
  const trackingTargetSequence = trackingTargetMedia
    ? railSequences.find((candidate) => candidate.id === trackingTargetMedia.sequenceId)
    : undefined;
  const trackingTargetMediaSource = trackingTargetMedia
    ? resolveStudioMediaSource(
        controller,
        trackingTargetMedia.sequenceId,
        trackingTargetMedia.streamId,
      )
    : null;
  const trackingTargetContextLabel = formatMediaContext(
    trackingTargetSequence?.label ?? trackingTargetMedia?.sequenceId,
    trackingTargetStream?.label ?? trackingTargetMedia?.streamId,
  );
  const trackingContextActive = Boolean(
    trackingTargetMedia &&
    trackingTargetMedia.sequenceId === snapshot.selectedSequenceId &&
    trackingTargetMedia.streamId === snapshot.selectedStreamId,
  );
  const playBrowseVideoFromLatestTrackingFrame = useCallback(() => {
    const latestTrackingFrame = controller.getSnapshot().trackingPreview.latestFrame;
    if (latestTrackingFrame) {
      playbackTransport.seekToSeconds(latestTrackingFrame.timestampSeconds);
    }
    controller.showVideo();
    playbackTransport.play();
  }, [controller, playbackTransport]);
  const trackingPreviewPlayback = useTrackingPreviewPlayback({
    preview: snapshot.trackingPreview,
    contextActive: trackingContextActive,
    mediaDurationSeconds:
      trackingTargetSequence?.durationSeconds ?? trackingTargetMediaSource?.durationSeconds,
    // 렌더 시점 read로 충분하다: tracking 활성 전이는 항상 controller 스냅샷 리렌더를
    // 동반하고, 그 시점의 브라우즈 비디오는 일시정지 상태라 시간이 흐르지 않는다.
    fallbackTimestampSeconds:
      frame?.timestampSeconds ?? playbackStore.getSnapshot().currentTimeSeconds,
    onPlay: () => void controller.startTrackingPreview?.(),
    onPause: controller.pauseTrackingPreview ?? controller.stopTrackingPreview,
  });
  const trackingPlaybackPresentationActive = trackingPreviewPlayback.active;
  const trackingTimelineActive = trackingPlaybackPresentationActive;
  const resultContextActive = Boolean(
    activeMediaResult &&
    activeMediaResult.targetMedia.sequenceId === snapshot.selectedSequenceId &&
    activeMediaResult.targetMedia.streamId === snapshot.selectedStreamId,
  );
  const timelineContextActive = activeMediaResult
    ? resultContextActive
    : trackingPlaybackPresentationActive
      ? trackingContextActive
      : true;
  // The same playback bar follows the active result or legacy tracking producer in either media
  // context. Natural completion returns ownership to the transport that was already underneath.
  const transport =
    activeMediaResult?.transport ??
    (trackingTimelineActive ? trackingPreviewPlayback.transport : playbackTransport);
  const handlePrimaryPlaybackToggle = useCallback(() => {
    if (trackingPreviewPlayback.transport.getSnapshot().playing) {
      trackingPreviewPlayback.transport.pause();
      return;
    }
    playBrowseVideoFromLatestTrackingFrame();
  }, [playBrowseVideoFromLatestTrackingFrame, trackingPreviewPlayback.transport]);
  const timelineConflictFrameIds = useMemo(() => {
    const frameIds = new Set(snapshot.saveConflictFrameIds ?? []);
    for (const issue of snapshot.annotationSetValidation?.issues ?? []) {
      if (issue.frameId) frameIds.add(issue.frameId);
      for (const frameId of issue.affectedFrameIds ?? []) frameIds.add(frameId);
      if (issue.location) frameIds.add(issue.location.frameId);
      for (const location of issue.affectedLocations ?? []) frameIds.add(location.frameId);
    }
    return frameIds;
  }, [snapshot.annotationSetValidation?.issues, snapshot.saveConflictFrameIds]);
  const annotationSetValidation = snapshot.annotationSetValidation ?? null;
  const trackingConditioningSetId = snapshot.draft?.base?.setId;
  const trackingConditioningStreamIds = useMemo(
    () => persistedTrackingConditioningStreamIds(workspace, trackingConditioningSetId),
    [trackingConditioningSetId, workspace],
  );
  const draftFrames = snapshot.draft?.frames;
  const draftDirtyScopes = snapshot.draft?.dirtyScopes;
  const blockingDirtyTrackingStreamIds = useMemo(() => {
    const streamIds = new Set<string>();
    if (!draftFrames || !draftDirtyScopes) return streamIds;
    for (const scope of draftDirtyScopes) {
      if (scope.kind !== "target" || timelineConflictFrameIds.has(scope.frameId)) continue;
      const streamId = draftFrames[scope.frameId]?.streamId;
      if (streamId) streamIds.add(streamId);
    }
    return streamIds;
  }, [draftDirtyScopes, draftFrames, timelineConflictFrameIds]);
  const {
    dirtyTargetIds,
    dirtyFrameIds,
    dirtyFrameIdsByStream: dirtyFrameIdsByCamera,
    dirtyFrameCountsByStream,
    dirtyFrameCountsBySequenceAndStream,
    saveConflictFrameIds,
    saveConflictFrameCountsByStream,
    saveConflictFrameCountsBySequenceAndStream,
    hasUnsavedAnnotationChanges,
    hasAnnotationContent,
    validationReviewDestination: annotationValidationReviewDestination,
    validationSummaryLocation: annotationValidationSummaryLocation,
    validationIssueLocations: annotationValidationIssueLocations,
    validationIssueFrameIds,
    validationIssueFrameCountsByStream,
    validationIssueFrameCountsBySequenceAndStream,
    savedAnnotationFrameIds,
  } = useAnnotationSetPresentation({
    workspace,
    draft: snapshot.draft,
    savedFrameIndex: snapshot.savedFrameIndex,
    sequenceDirectory,
    pinnedSequence: snapshot.pinnedSequence,
    frame,
    validation: annotationSetValidation,
    saveConflictFrameIds: snapshot.saveConflictFrameIds ?? [],
  });
  const {
    allFrames: capturedFramesAcrossStreams,
    streamFrames: capturedFrames,
    capturedFrameCountsByStream,
    savedSequenceContentCountsByStream,
    unannotatedFrameCountsBySequenceAndStream,
    annotatedFrameCountsBySequenceAndStream,
    frameAtPlayhead,
    findFrameAtTimelineTime,
    timelineMarkers: playbackTimelineMarkers,
  } = useCapturedFrameCollection({
    draft: snapshot.draft,
    savedFrameIndex: snapshot.savedFrameIndex,
    selectedSequenceId: snapshot.selectedSequenceId,
    selectedStreamId: snapshot.selectedStreamId,
    timelineSequenceId: activeMediaResult?.targetMedia.sequenceId ?? snapshot.selectedSequenceId,
    timelineStreamId: activeMediaResult?.targetMedia.streamId ?? snapshot.selectedStreamId,
    viewMode: activeMediaResult ? "browse" : studioViewMode,
    timelineTransport: transport,
    highlightActiveFrameWhilePlaying: activeMediaResult === null,
    authoring,
    locale,
    conflictFrameIds: timelineConflictFrameIds,
    dirtyFrameIds,
  });
  const selectedEpisodeCapturedFrames = useMemo(
    () =>
      capturedFrames.filter(
        ({ frame: capturedFrame }) => capturedFrame.sequenceId === snapshot.selectedSequenceId,
      ),
    [capturedFrames, snapshot.selectedSequenceId],
  );
  useEffect(() => {
    if (snapshot.selectedSequenceId === null) {
      setCapturedFrameScope("all-camera");
    }
  }, [snapshot.selectedSequenceId]);
  const selectedEpisodeFilterActive =
    capturedFrameScope === "selected-episode" && snapshot.selectedSequenceId !== null;
  const effectiveCapturedFrameScope = selectedEpisodeFilterActive
    ? capturedFrameScope
    : "all-camera";
  const displayedCapturedFrames = selectedEpisodeFilterActive
    ? selectedEpisodeCapturedFrames
    : capturedFrames;
  const allCapturedFrameIds = useMemo(
    () => capturedFramesAcrossStreams.map(({ frame: item }) => item.frameId),
    [capturedFramesAcrossStreams],
  );
  const annotationContextActive = Boolean(frame && mediaContext.kind === "captured-frame");
  const annotationToolsVisible = annotationContextActive;
  const toolsPresentation = useAnnotationToolsPresentation(
    annotationContextActive,
    workspace.targets.length,
    authoring,
  );
  const {
    page: capturedFramePage,
    pageCount: capturedFramePageCount,
    visibleFrames: visibleCapturedFrames,
    listViewportRef: capturedFrameListViewportRef,
    listRef: capturedFrameListRef,
    selectedFrameId: selectedCapturedFrameId,
    setSelectedFrameId: setSelectedCapturedFrameId,
    previewFrameId,
    previewFrame,
    setPreviewFrameId,
    revealFrame: revealCapturedFrame,
    togglePreview: toggleCapturedFramePreview,
    previousPage: showPreviousCapturedFramePage,
    nextPage: showNextCapturedFramePage,
  } = useCapturedFramePresentation({
    collectionId: workspace.collectionId,
    // The visible list remains camera-scoped, while preview identity is workspace-scoped so an
    // explicitly opened reference survives camera navigation until its source disappears.
    capturedFrames: capturedFramesAcrossStreams,
    displayedFrames: displayedCapturedFrames,
    allCapturedFrameIds,
    toolsLayoutKey: `${annotationContextActive}:${toolsPresentation.compact}`,
    activeFrameId: snapshot.draft?.activeFrameId ?? null,
  });
  const previewOutsideFrameFilter =
    previewFrame !== undefined &&
    !displayedCapturedFrames.some(
      ({ frame: displayedFrame }) => displayedFrame.frameId === previewFrame.frameId,
    );
  const materializedPreviewFrame =
    previewFrame && "targets" in previewFrame ? previewFrame : undefined;
  const previewFrameMedia = materializedPreviewFrame
    ? resolveStudioMediaSource(
        controller,
        materializedPreviewFrame.sequenceId,
        materializedPreviewFrame.streamId,
      )
    : null;
  const previousFrameListContextRef = useRef({
    sequenceId: snapshot.selectedSequenceId,
    streamId: snapshot.selectedStreamId,
    filterActive: selectedEpisodeFilterActive,
  });
  useEffect(() => {
    const nextContext = {
      sequenceId: snapshot.selectedSequenceId,
      streamId: snapshot.selectedStreamId,
      filterActive: selectedEpisodeFilterActive,
    };
    const previousContext = previousFrameListContextRef.current;
    if (
      previousContext.sequenceId === nextContext.sequenceId &&
      previousContext.streamId === nextContext.streamId &&
      previousContext.filterActive === nextContext.filterActive
    ) {
      return;
    }
    previousFrameListContextRef.current = nextContext;
    const activeContextFrame = displayedCapturedFrames.find(
      ({ frame: displayedFrame }) =>
        displayedFrame.frameId === snapshot.draft?.activeFrameId &&
        displayedFrame.sequenceId === snapshot.selectedSequenceId &&
        displayedFrame.streamId === snapshot.selectedStreamId,
    );
    const firstContextFrame = displayedCapturedFrames.find(
      ({ frame: displayedFrame }) =>
        displayedFrame.sequenceId === snapshot.selectedSequenceId &&
        displayedFrame.streamId === snapshot.selectedStreamId,
    );
    const frameToReveal = activeContextFrame ?? firstContextFrame;
    if (frameToReveal) {
      revealCapturedFrame(frameToReveal.frame.frameId);
    }
  }, [
    displayedCapturedFrames,
    revealCapturedFrame,
    selectedEpisodeFilterActive,
    snapshot.draft?.activeFrameId,
    snapshot.selectedSequenceId,
    snapshot.selectedStreamId,
  ]);
  const resultSequence = activeMediaResult
    ? railSequences.find((candidate) => candidate.id === activeMediaResult.targetMedia.sequenceId)
    : undefined;
  const resultStream = activeMediaResult
    ? workspace.streams.find((candidate) => candidate.id === activeMediaResult.targetMedia.streamId)
    : undefined;
  const viewportSequence = activeMediaResult
    ? resultSequence
    : mediaContext.kind === "captured-frame" && frame
      ? railSequences.find((candidate) => candidate.id === frame.sequenceId)
      : sequence;
  const capturedFrameStream = frame
    ? workspace.streams.find((candidate) => candidate.id === frame.streamId)
    : undefined;
  const capturedFrameMedia = frame
    ? resolveStudioMediaSource(controller, frame.sequenceId, frame.streamId)
    : null;
  const viewportStream = activeMediaResult
    ? resultStream
    : mediaContext.kind === "captured-frame" && frame
      ? capturedFrameStream
      : stream;
  const capturedFrameResource = useCapturedFrameRenderResource(
    controller,
    frame?.frameSource ?? null,
  );
  const viewportContextLabel = formatMediaContext(
    viewportSequence?.label ??
      activeMediaResult?.targetMedia.sequenceId ??
      frame?.sequenceId ??
      snapshot.selectedSequenceId,
    viewportStream?.label ??
      activeMediaResult?.targetMedia.streamId ??
      frame?.streamId ??
      snapshot.selectedStreamId,
  );
  const capturedFrameCameraLabel = stream?.label ?? snapshot.selectedStreamId ?? "—";
  const activeFrameHasUnsavedAnnotationChanges = dirtyTargetIds.size > 0;
  const currentCameraDirtyFrameIds =
    snapshot.selectedStreamId === null
      ? []
      : (dirtyFrameIdsByCamera.get(snapshot.selectedStreamId) ?? []);
  const removingCapturedFrame = snapshot.operations.remove.pending;
  const frameTargets = useMemo(
    () =>
      frame
        ? workspace.targets
            .map((candidate) => ({ target: candidate, draft: frame.targets[candidate.id] }))
            .filter(({ target: candidate }) => visibleTargetIds.has(candidate.id))
        : [],
    [frame, visibleTargetIds, workspace.targets],
  );
  const globalStreams = useMemo(
    () =>
      sequenceDirectory
        ? workspace.streams
        : workspace.streams.filter((candidate) =>
            workspace.sequences.some((workspaceSequence) =>
              workspaceSequence.streamIds.includes(candidate.id),
            ),
          ),
    [sequenceDirectory, workspace.sequences, workspace.streams],
  );
  const globalStreamIds = useMemo(
    () => new Set(globalStreams.map((candidate) => candidate.id)),
    [globalStreams],
  );
  const {
    exitActionState,
    exitValidationReviewRequested,
    exitError,
    cancelExitValidationReview,
    pendingValidationReviewRequest,
    cancelValidationReview,
    approveValidationReviewOverride,
    exitStudio,
    pendingStreamChange: pendingCameraChange,
    streamChangeSaving: cameraChangeSaving,
    streamChangeError: cameraChangeError,
    requestStreamChange: requestCameraChange,
    closeStreamChange: closeCameraChange,
    discardAndChangeStream: discardAndChangeCamera,
    saveAndChangeStream: saveAndChangeCamera,
    currentStreamSavePending: currentCameraSavePending,
    currentStreamSaveError: currentCameraSaveError,
    saveCurrentStream: saveCurrentCamera,
  } = useStudioNavigationGuards({
    controller,
    locale,
    onExit,
    onExitRequestSettled,
    validationReviewRequestId,
    onValidationReviewSettled,
    selectedStreamId: snapshot.selectedStreamId,
    availableStreamIds: globalStreamIds,
    dirtyFrameIdsByStream: dirtyFrameIdsByCamera,
    onChangeStream: (streamId) => {
      const currentSnapshot = controller.getSnapshot();
      if (sequenceDirectory) {
        setSelectedCapturedFrameId(null);
        cancelCapturedFrameSeek();
        playbackTransport.reset();
        controller.selectStream(streamId);
        return;
      }
      const currentSequence = railSequences.find(
        (candidate) => candidate.id === currentSnapshot.selectedSequenceId,
      );
      const nextSequence =
        currentSequence?.streamIds.includes(streamId) === true
          ? currentSequence
          : railSequences.find((candidate) => candidate.streamIds.includes(streamId));
      if (!nextSequence) return;
      setSelectedCapturedFrameId(null);
      if (
        nextSequence.id !== currentSnapshot.selectedSequenceId ||
        streamId !== currentSnapshot.selectedStreamId
      ) {
        cancelCapturedFrameSeek();
        playbackTransport.reset();
      }
      if (nextSequence.id !== currentSnapshot.selectedSequenceId) {
        controller.selectSequence(nextSequence.id);
      }
      if (controller.getSnapshot().selectedStreamId !== streamId) {
        controller.selectStream(streamId);
      }
      controller.showVideo();
    },
  });
  const frameEditingStatusState = !authoring
    ? "viewing"
    : snapshot.operations.save.pending
      ? "saving"
      : activeFrameHasUnsavedAnnotationChanges
        ? "dirty"
        : "idle";
  const {
    pendingTargets: pendingSegmentationTargets,
    queuedOrPending: segmentationQueuedOrPending,
  } = useAnnotationSegmentation({
    controller,
    // Segmentation is a display derivation, not a draft-wide completion pass. Only the frame that
    // is actually open for Annotation should consume SAM; its pending slots are batched below.
    enabled: snapshot.connected && segmentationAvailable && studioViewMode === "annotate",
    draft: snapshot.draft,
    targets: workspace.targets,
    operation: snapshot.operations.segment,
  });
  const pendingSegmentationTargetIds = useMemo(
    () =>
      new Set(
        pendingSegmentationTargets?.flatMap((target) =>
          target.frameId === frame?.frameId ? [target.targetId] : [],
        ) ?? [],
      ),
    [frame?.frameId, pendingSegmentationTargets],
  );
  const annotationPanelVisible =
    authoring ||
    studioViewMode === "annotate" ||
    capturedFrames.length > 0 ||
    previewFrame !== undefined;
  const capturedFrameRemovalDisabled =
    !authoringOperationsAvailable ||
    currentCameraSavePending ||
    segmentationQueuedOrPending ||
    snapshot.operations.capture.pending ||
    snapshot.operations.save.pending ||
    removingCapturedFrame;
  useEffect(() => {
    if (
      frameRemovalConfirmationId !== null &&
      !snapshot.draft?.frames[frameRemovalConfirmationId]
    ) {
      setFrameRemovalConfirmationId(null);
    }
  }, [frameRemovalConfirmationId, snapshot.draft?.frames]);

  useEffect(() => {
    if (annotationToolsVisible) return;
    setClearAllConfirmationOpen(false);
    setDiscardChangesConfirmationOpen(false);
  }, [annotationToolsVisible]);

  const ensureTargetVisibleForEditing = (targetId: string) => {
    setTargetVisibility(targetId, true);
  };

  const addTargetPoint = (kind: AnnotationPointKind, pointX: number, pointY: number) => {
    if (snapshot.selectedTargetId !== null) {
      ensureTargetVisibleForEditing(snapshot.selectedTargetId);
    }
    controller.addPoint(kind, pointX, pointY);
  };

  const removeTargetPoint = (targetId: string, pointId: string) => {
    ensureTargetVisibleForEditing(targetId);
    if (snapshot.selectedTargetId !== targetId) controller.selectTarget(targetId);
    controller.removePoint(pointId);
  };

  const handleTimelineDurationChange = useCallback(
    (durationSeconds: number) => playbackStore.setSnapshot({ durationSeconds }),
    [playbackStore],
  );
  const handleTimelineCurrentTimeChange = useCallback(
    (currentTimeSeconds: number) => playbackStore.setSnapshot({ currentTimeSeconds }),
    [playbackStore],
  );
  const handleTimelineStatusChange = useCallback(
    // "idle"은 not-ready 텔레메트리다 — annotate에서 첫 재생 명령으로 비디오가 이제 막
    // 마운트된 경우, 여기서 playing을 끄면 재생 의도가 metadata 로드 전에 소멸한다.
    (status: VideoStatus) =>
      playbackStore.setSnapshot(
        status === "idle"
          ? { status }
          : {
              status,
              playing: status === "playing" || status === "buffering" || status === "waiting",
            },
      ),
    [playbackStore],
  );
  const handleTimelineEnded = useCallback(
    () => playbackStore.setSnapshot({ playing: false, status: "ended" }),
    [playbackStore],
  );

  const {
    editFrame: editCapturedFrame,
    navigateFrame: navigateToCapturedFrame,
    openReviewFrame: openAnnotationReviewFrame,
    openValidationIssue: openAnnotationSetValidationIssue,
    selectFrameForActions: selectCapturedFrameForActions,
    materializeFrame: materializeCapturedFrame,
    materializingFrameId,
    materializationFailure,
    showVideo,
  } = useCapturedFrameActions({
    controller,
    previewFrameId,
    playbackTransport: transport,
    setSelectedFrameId: setSelectedCapturedFrameId,
    setPreviewFrameId,
    revealFrame: revealCapturedFrame,
    requestFrameSeek: requestCapturedFrameSeek,
    selectTargetForReview: (targetId) => {
      ensureTargetVisibleForEditing(targetId);
      selectTarget(targetId);
    },
  });
  useEffect(() => {
    const retainedFrameIds = [previewFrameId, frameRemovalConfirmationId].filter(
      (frameId): frameId is string => frameId !== null,
    );
    controller.setRetainedMaterializedFrameIds?.(retainedFrameIds);
    return () => controller.setRetainedMaterializedFrameIds?.([]);
  }, [controller, frameRemovalConfirmationId, previewFrameId]);
  const requestCapturedFrameRemoval = useCallback(
    (frameId: string) => {
      if (controller.getSnapshot().draft?.frames[frameId]) {
        setFrameRemovalConfirmationId(frameId);
        return;
      }
      void materializeCapturedFrame(frameId).then((materializedFrame) => {
        if (materializedFrame) setFrameRemovalConfirmationId(frameId);
      });
    },
    [controller, materializeCapturedFrame],
  );
  const captureFrame = async (timestampSeconds = transport.getSnapshot().currentTimeSeconds) => {
    // Playing keeps the action group stable, so guard the mutation itself against a frame that
    // shares the visible tenth-second timeline position. This prevents a duplicate-looking
    // backend capture without flashing edit/remove controls as the playhead passes each marker.
    if (findFrameAtTimelineTime(timestampSeconds)) return;
    const captureStartedWhilePlaying = transport.getSnapshot().playing;
    const previousFrameIds = new Set(controller.getSnapshot().draft?.orderedFrameIds ?? []);

    await controller.captureFrame(timestampSeconds);

    const currentDraft = controller.getSnapshot().draft;
    const createdFrameId = currentDraft?.activeFrameId;
    if (
      currentDraft === null ||
      createdFrameId === null ||
      createdFrameId === undefined ||
      previousFrameIds.has(createdFrameId) ||
      !currentDraft.frames[createdFrameId]
    ) {
      return;
    }

    const createdFrame = currentDraft.frames[createdFrameId];
    if (
      Math.abs(createdFrame.timestampSeconds - timestampSeconds) >
      CAPTURE_TIMESTAMP_NOTICE_THRESHOLD_SECONDS + Number.EPSILON
    ) {
      studioToastManager.info(
        captureTimestampAdjustedMessage(
          locale,
          formatMediaPlaybackTime(createdFrame.timestampSeconds),
        ),
      );
    }

    // Capture already makes the new frame active in the controller. A paused capture follows the
    // same captured-frame activation path as marker/list selection and opens Annotation at the
    // backend-resolved timestamp. Never interrupt playback that was already running or is running
    // when capture completes.
    setSelectedCapturedFrameId(createdFrameId);
    if (captureStartedWhilePlaying || transport.getSnapshot().playing) return;
    navigateToCapturedFrame(createdFrameId);
  };
  const selectStudioSequence = (sequenceId: string): boolean => {
    const currentSnapshot = controller.getSnapshot();
    if (sequenceId === currentSnapshot.selectedSequenceId) return true;
    if (currentCameraSavePending) return false;
    setSelectedCapturedFrameId(null);
    cancelCapturedFrameSeek();
    playbackTransport.reset();
    controller.selectSequence(sequenceId);
    controller.showVideo();
    return true;
  };
  const showContextVideo = () => {
    showVideo();
  };
  const studioTimelineTransport = useMemo<PlaybackTransport>(
    () =>
      activeMediaResult
        ? activeMediaResult.transport
        : trackingTimelineActive
          ? trackingPreviewPlayback.transport
          : {
              subscribe: playbackTransport.subscribe,
              getSnapshot: playbackTransport.getSnapshot,
              play: () => {
                showVideo();
                playbackTransport.play();
              },
              pause: playbackTransport.pause,
              toggle: () => {
                if (!playbackTransport.getSnapshot().playing) showVideo();
                playbackTransport.toggle();
              },
              seekToSeconds: (seconds) => {
                showVideo();
                playbackTransport.seekToSeconds(seconds);
              },
              setPlaybackRate: playbackTransport.setPlaybackRate,
              reset: () => {
                showVideo();
                playbackTransport.reset();
              },
            },
    [
      activeMediaResult,
      playbackTransport,
      showVideo,
      trackingTimelineActive,
      trackingPreviewPlayback.transport,
    ],
  );
  const openSequenceInVideo = (sequenceId: string) => {
    if (selectStudioSequence(sequenceId)) showContextVideo();
  };
  const openStreamInVideo = (streamId: string) => {
    requestCameraChange(streamId, showContextVideo);
  };
  const requestValidationPopup = useCallback(() => {
    setValidationPopupOpenRequestId((current) => current + 1);
  }, []);

  useAnnotationValidationGuidanceToast({
    manager: studioToastManager,
    validation: annotationSetValidation,
    locale,
    onReviewValidation: requestValidationPopup,
  });
  useAnnotationOperationErrorToast({
    manager: studioToastManager,
    error: snapshot.operations.capture.error,
    locale,
  });
  useAnnotationOperationErrorToast({
    manager: studioToastManager,
    error: snapshot.operations.segment.error,
    locale,
  });
  const saveErrorOwnedByContext =
    snapshot.operations.save.error?.conflict === true ||
    pendingCameraChange !== null ||
    currentCameraSavePending ||
    currentCameraSaveError !== null ||
    exitActionState !== "idle" ||
    exitError !== null;
  useAnnotationOperationErrorToast({
    manager: studioToastManager,
    error: snapshot.operations.save.error,
    locale,
    suppressed: saveErrorOwnedByContext,
  });
  useAnnotationOperationErrorToast({
    manager: studioToastManager,
    error: snapshot.operations.remove.error,
    locale,
  });
  useAnnotationOperationErrorToast({
    manager: studioToastManager,
    error: snapshot.operations.render.error,
    locale,
  });
  const currentSaveErrorMessage = snapshot.operations.save.error
    ? (resolveAnnotationOperationErrorMessage(snapshot.operations.save.error, locale) ??
      annotationStudioText("operationFailed", locale))
    : null;
  const saveConflictOwnsExitFailure =
    snapshot.operations.save.error?.conflict === true && exitError === currentSaveErrorMessage;
  const saveConflictOwnsCurrentCameraFailure =
    snapshot.operations.save.error?.conflict === true &&
    currentCameraSaveError === currentSaveErrorMessage;
  useStudioErrorToast({
    manager: studioToastManager,
    notification: exitError ? { signature: `exit:${exitError}`, message: exitError } : null,
    suppressed: saveConflictOwnsExitFailure,
  });
  useStudioErrorToast({
    manager: studioToastManager,
    notification: currentCameraSaveError
      ? {
          signature: `current-camera-save:${currentCameraSaveError}`,
          message: currentCameraSaveError,
        }
      : null,
    suppressed: saveConflictOwnsCurrentCameraFailure,
  });
  useStudioErrorToast({
    manager: studioToastManager,
    notification: materializationFailure
      ? {
          signature: `captured-frame-load:${materializationFailure.frameId}:${materializationFailure.attempt}`,
          message: annotationStudioText("capturedFrameLoadFailed", locale),
        }
      : null,
  });
  useTrackingPreviewErrorToast({
    manager: studioToastManager,
    status: snapshot.trackingPreview.status,
    targetMedia: snapshot.trackingPreview.targetMedia,
    error: snapshot.trackingPreview.error,
    locale,
    mediaContext: trackingTargetContextLabel,
  });

  const { enabled: trackingEnabled } = resolveTrackingPreviewAvailability({
    locale,
    trackingAvailable: snapshot.trackingAvailable,
    productAvailability: snapshot.trackingAvailability,
    targetMedia: selectedTrackingTargetMedia,
    hasUnsavedChanges:
      selectedTrackingTargetMedia !== null &&
      blockingDirtyTrackingStreamIds.has(selectedTrackingTargetMedia.streamId),
    hasPersistedConditioning:
      snapshot.trackingUsesCanonicalConditioning ||
      (selectedTrackingTargetMedia !== null &&
        trackingConditioningStreamIds.has(selectedTrackingTargetMedia.streamId)),
    validationStatus: annotationSetValidation?.status,
    validationAvailability: annotationSetValidation?.availability,
    segmentationQueuedOrPending,
    savePending: snapshot.operations.save.pending || currentCameraSavePending,
    trackingPending: snapshot.operations.track.pending,
  });
  const controllerResolvesTrackingAvailability =
    controller.getTrackingPreviewAvailability !== undefined;
  const fallbackTrackingAvailability = controllerResolvesTrackingAvailability
    ? undefined
    : snapshot.trackingAvailability;
  const fallbackTrackingSequenceId = controllerResolvesTrackingAvailability
    ? null
    : snapshot.selectedSequenceId;
  const trackingDraftAvailable =
    snapshot.draft !== null || snapshot.trackingUsesCanonicalConditioning;
  const { trackableSequenceIds, trackableStreamIds, runnableTrackingSequenceIds } = useMemo(() => {
    const sequenceIds = new Set<string>();
    const streamIds = new Set<string>();
    const runnableSequenceIds = new Set<string>();
    if (!snapshot.trackingAvailable || !trackingDraftAvailable) {
      return {
        trackableSequenceIds: sequenceIds,
        trackableStreamIds: streamIds,
        runnableTrackingSequenceIds: runnableSequenceIds,
      };
    }

    for (const candidateSequence of railSequences) {
      for (const candidateStreamId of candidateSequence.streamIds) {
        if (!globalStreamIds.has(candidateStreamId)) continue;
        const targetMedia = {
          sequenceId: candidateSequence.id,
          streamId: candidateStreamId,
        };
        const productAvailability =
          controller.getTrackingPreviewAvailability?.(targetMedia) ??
          (candidateSequence.id === fallbackTrackingSequenceId &&
          candidateStreamId === snapshot.selectedStreamId
            ? fallbackTrackingAvailability
            : undefined);
        const availability = resolveTrackingPreviewAvailability({
          locale,
          trackingAvailable: true,
          productAvailability,
          targetMedia,
          hasUnsavedChanges: blockingDirtyTrackingStreamIds.has(candidateStreamId),
          hasPersistedConditioning:
            snapshot.trackingUsesCanonicalConditioning ||
            trackingConditioningStreamIds.has(candidateStreamId),
          validationStatus: annotationSetValidation?.status,
          validationAvailability: annotationSetValidation?.availability,
          // Transient operation state is represented by trackingStartBlocked below so these
          // structural sets retain identity and the episode controls stay mounted.
          segmentationQueuedOrPending: false,
          savePending: false,
          trackingPending: false,
        });
        if (!availability.capable) continue;
        streamIds.add(candidateStreamId);
        if (candidateStreamId === snapshot.selectedStreamId) {
          sequenceIds.add(candidateSequence.id);
          if (availability.enabled) runnableSequenceIds.add(candidateSequence.id);
        }
      }
    }
    return {
      trackableSequenceIds: sequenceIds,
      trackableStreamIds: streamIds,
      runnableTrackingSequenceIds: runnableSequenceIds,
    };
  }, [
    annotationSetValidation?.availability,
    annotationSetValidation?.status,
    blockingDirtyTrackingStreamIds,
    controller,
    fallbackTrackingAvailability,
    fallbackTrackingSequenceId,
    globalStreamIds,
    locale,
    snapshot.selectedStreamId,
    snapshot.trackingAvailable,
    snapshot.trackingUsesCanonicalConditioning,
    trackingDraftAvailable,
    trackingConditioningStreamIds,
    railSequences,
  ]);
  const trackingStartBlocked =
    segmentationQueuedOrPending ||
    snapshot.operations.save.pending ||
    currentCameraSavePending ||
    snapshot.operations.track.pending;
  const startTrackingPreview = (targetMedia: TrackingPreviewTargetMedia) => {
    playbackTransport.pause();
    // Let the pressed/hover state paint before the controller's synchronous external-store
    // commit. The following task starts tracking and renders the canonical pending Spinner.
    scheduleAfterInteractionPaint(() => {
      void controller.startTrackingPreview(targetMedia);
    });
  };
  const pauseTrackingPreview = () => {
    cancelPendingInteractionTask();
    if (trackingPreviewPlayback.active) {
      const trackingTimeline = trackingPreviewPlayback.transport.getSnapshot();
      playbackTransport.setPlaybackRate(trackingTimeline.playbackRate);
      playbackTransport.seekToSeconds(trackingTimeline.currentTimeSeconds);
    }
    (controller.pauseTrackingPreview ?? controller.stopTrackingPreview)();
  };
  const startSelectedTrackingPreview = () => {
    if (selectedTrackingTargetMedia) startTrackingPreview(selectedTrackingTargetMedia);
  };
  const startEpisodeTrackingPreview = (sequenceId: string) => {
    const streamId = snapshot.selectedStreamId;
    if (streamId === null || trackingStartBlocked || !runnableTrackingSequenceIds.has(sequenceId)) {
      return;
    }
    if (sequenceId !== controller.getSnapshot().selectedSequenceId) {
      setSelectedCapturedFrameId(null);
      cancelCapturedFrameSeek();
      playbackTransport.reset();
    }
    startTrackingPreview({ sequenceId, streamId });
  };
  const trackingStatusForCurrentContext = trackingContextActive
    ? snapshot.trackingPreview.status
    : "idle";
  const trackingPreviewRunning =
    snapshot.trackingPreview.status === "starting" || snapshot.trackingPreview.status === "running";
  const trackingRunningForCurrentContext = trackingContextActive && trackingPreviewRunning;
  const trackingControlVisible =
    trackingEnabled || trackingRunningForCurrentContext || trackingPlaybackPresentationActive;
  const studioShortcutBlocked =
    clearAllConfirmationOpen ||
    discardChangesConfirmationOpen ||
    pendingCameraChange !== null ||
    frameRemovalConfirmationId !== null ||
    snapshot.leaveConfirmation !== null;
  const saveShortcutAvailable =
    authoringOperationsAvailable &&
    currentCameraDirtyFrameIds.length > 0 &&
    !currentCameraSavePending &&
    !cameraChangeSaving &&
    exitActionState === "idle" &&
    !segmentationQueuedOrPending &&
    !snapshot.operations.capture.pending &&
    !snapshot.operations.save.pending &&
    !removingCapturedFrame;
  const captureShortcutAvailable =
    timelineContextActive &&
    authoringOperationsAvailable &&
    Boolean(activeMediaResult?.source.baseMedia.src || selectedMedia) &&
    frameAtPlayhead === undefined &&
    !snapshot.operations.capture.pending &&
    !currentCameraSavePending;
  const annotationOpenShortcutAvailable =
    timelineContextActive &&
    studioViewMode === "browse" &&
    frameAtPlayhead !== undefined &&
    (!authoring || (authoringOperationsAvailable && !currentCameraSavePending));
  const removableMarkerId =
    timelineContextActive &&
    !trackingPreviewPlayback.live &&
    authoringOperationsAvailable &&
    controller.removeCapturedFrame !== undefined &&
    frameAtPlayhead !== undefined &&
    !capturedFrameRemovalDisabled
      ? frameAtPlayhead.frameId
      : null;
  const studioTimelineShortcuts = useMediaTimelineShortcuts({
    transport: studioTimelineTransport,
    stepSeconds: 0.1,
    disabled: studioShortcutBlocked || !(activeMediaResult?.source.baseMedia.src || selectedMedia),
    onMarkerCreate: captureShortcutAvailable ? captureFrame : undefined,
    onMarkerRemove: removableMarkerId === null ? undefined : requestCapturedFrameRemoval,
    removableMarkerId,
    commands: STUDIO_TIMELINE_SHORTCUT_COMMANDS,
  });
  const studioTargetShortcuts = useAnnotationTargetShortcuts({
    targets: workspace.targets,
    selectedTargetId: snapshot.selectedTargetId,
    selectedOnly: currentTargetPresentation.selectedOnly,
    disabled:
      studioShortcutBlocked || !annotationToolsVisible || (authoring && currentCameraSavePending),
    onSelectedOnlyChange: setSelectedOnly,
    onVisibleTargetIdsChange: setVisibleTargets,
    onTargetChange: selectTarget,
  });
  const studioCommandShortcuts = useAnnotationStudioShortcuts({
    blocked: studioShortcutBlocked,
    authoringBlocked: currentCameraSavePending || !authoringOperationsAvailable,
    authoring,
    canUndo: snapshot.canUndo,
    canRedo: snapshot.canRedo,
    saveAvailable: saveShortcutAvailable,
    annotationOpenAvailable: annotationOpenShortcutAvailable,
    onUndo: controller.undo,
    onRedo: controller.redo,
    onSave: () => void saveCurrentCamera(),
    onAnnotationOpen: () => {
      if (frameAtPlayhead) editCapturedFrame(frameAtPlayhead.frameId);
    },
  });
  const studioCommandShortcutHandlerRef = useRef(studioCommandShortcuts);
  studioCommandShortcutHandlerRef.current = studioCommandShortcuts;
  const studioTargetShortcutHandlerRef = useRef(studioTargetShortcuts.onKeyDown);
  studioTargetShortcutHandlerRef.current = studioTargetShortcuts.onKeyDown;
  const studioTimelineShortcutHandlerRef = useRef(studioTimelineShortcuts.onKeyDown);
  studioTimelineShortcutHandlerRef.current = studioTimelineShortcuts.onKeyDown;
  useEffect(() => {
    const ownerDocument = studioRootRef.current?.ownerDocument;
    if (!ownerDocument) return;

    // Listen after bubbling so nested controls get first refusal via preventDefault/propagation,
    // while page siblings in the same document can still drive the mounted Studio timeline.
    const handleDocumentStudioShortcut = (event: KeyboardEvent) => {
      if (
        isAnnotationStudioTextEditingTarget(event.target) ||
        event.isComposing ||
        event.keyCode === 229
      ) {
        return;
      }

      studioCommandShortcutHandlerRef.current(event);
      studioTargetShortcutHandlerRef.current(event);
      // Vertical arrows remain owned by focused lists, menus and native controls. The timeline's
      // local range input still handles them when it owns focus.
      if (event.key === "ArrowUp" || event.key === "ArrowDown") return;
      studioTimelineShortcutHandlerRef.current(event);
    };

    ownerDocument.addEventListener("keydown", handleDocumentStudioShortcut);
    return () => ownerDocument.removeEventListener("keydown", handleDocumentStudioShortcut);
  }, []);
  const studioAriaKeyShortcuts = [
    studioTimelineShortcuts.ariaKeyShortcuts.includes("Space") ? "Space" : "",
    !studioShortcutBlocked && selectedMedia ? "ArrowLeft ArrowRight" : "",
    annotationOpenShortcutAvailable || studioTimelineShortcuts.ariaKeyShortcuts.includes("Shift+C")
      ? "Shift+C"
      : "",
    studioTimelineShortcuts.ariaKeyShortcuts.includes("Delete") ? "Delete" : "",
    !studioShortcutBlocked && authoringOperationsAvailable && !currentCameraSavePending
      ? "Control+S Meta+S"
      : "",
  ]
    .filter(Boolean)
    .join(" ");
  // memo 경계(StudioContextRail·CapturedFramePanel·AnnotationPointActions·AnnotationToolsPanel·
  // StudioCommandBar·StudioMediaControls)를 넘는 함수/배열/element prop은 identity를 고정한다 —
  // 렌더마다 재생성되면 memo가 무효화된다.
  const handleValidate = useStableHandler(() => void controller.validateAnnotationSet?.());
  const handleOpenValidationIssue = useStableHandler(openAnnotationSetValidationIssue);
  const handleOpenValidationReview = useStableHandler(openAnnotationReviewFrame);
  const handleCancelExitReview = useStableHandler(cancelExitValidationReview);
  const handleExitAnyway = useStableHandler(() => void exitStudio({ skipValidation: true }));
  const handleExit = useStableHandler(() => void exitStudio());
  const handleExitRequest = useStableHandler((requestId: number) => void exitStudio({ requestId }));
  const previousExitRequestIdRef = useRef(exitRequestId);
  useEffect(() => {
    const previousExitRequestId = previousExitRequestIdRef.current;
    previousExitRequestIdRef.current = exitRequestId;
    if (
      exitRequestId === undefined ||
      exitRequestId === previousExitRequestId ||
      onExit === undefined
    ) {
      return;
    }
    handleExitRequest(exitRequestId);
  }, [exitRequestId, handleExitRequest, onExit]);
  const handleSelectSequence = useStableHandler(selectStudioSequence);
  const handleSelectStream = useStableHandler(requestCameraChange);
  const handleOpenSequenceInVideo = useStableHandler(openSequenceInVideo);
  const handleOpenStreamInVideo = useStableHandler(openStreamInVideo);
  const handleStartEpisodeTrackingPreview = useStableHandler(startEpisodeTrackingPreview);
  const handleStartSelectedTrackingPreview = useStableHandler(startSelectedTrackingPreview);
  const handleStopTrackingPreview = useStableHandler(pauseTrackingPreview);
  const handleMarkerSelect = useStableHandler(navigateToCapturedFrame);
  const handleToggleCapturedFramePreview = useStableHandler((frameId: string) => {
    if (controller.getSnapshot().draft?.frames[frameId]) {
      toggleCapturedFramePreview(frameId);
      return;
    }
    void materializeCapturedFrame(frameId).then((materializedFrame) => {
      if (materializedFrame) toggleCapturedFramePreview(frameId);
    });
  });
  const handleCloseCapturedFramePreview = useStableHandler(() => {
    const inlinePreview = studioRootRef.current?.querySelector("[data-preview-layout='inline']");
    if (inlinePreview?.contains(document.activeElement)) {
      const row = Array.from(
        capturedFrameListRef.current?.querySelectorAll<HTMLElement>("[data-captured-frame-row]") ??
          [],
      ).find((item) => item.dataset.frameId === previewFrameId);
      row?.querySelector<HTMLElement>("[data-frame-preview]")?.focus();
    }
    setPreviewFrameId(null);
  });
  const handleEditCapturedFrame = useStableHandler(editCapturedFrame);
  const handleBrowseVideoRequest = useStableHandler(showContextVideo);
  const handleCapture = useStableHandler(captureFrame);
  const handleSaveCameraAnnotations = useStableHandler(() => void saveCurrentCamera());
  const handleSelectFrameForActions = useStableHandler(selectCapturedFrameForActions);
  const handlePreviousCapturedFramePage = useStableHandler(showPreviousCapturedFramePage);
  const handleNextCapturedFramePage = useStableHandler(showNextCapturedFramePage);
  const handleAddPoint = useStableHandler(addTargetPoint);
  const handleRemovePoint = useStableHandler(removeTargetPoint);
  const handleSelectedOnlyChange = useStableHandler(setSelectedOnly);
  const handleVisibleTargetIdsChange = useStableHandler(setVisibleTargets);
  const handleTargetChange = useStableHandler(selectTarget);
  const handleTargetNameChange = useStableHandler(setTargetName);
  const handleRequestClearAllTargets = useStableHandler(() => setClearAllConfirmationOpen(true));
  const handleRequestDiscardChanges = useStableHandler(() =>
    setDiscardChangesConfirmationOpen(true),
  );
  const handleSaveActiveFrame = useStableHandler(() => {
    if (frame) void controller.saveAnnotationFrame(frame.frameId);
  });
  const capturedFrameList = useMemo(
    () => capturedFrames.map(({ frame: capturedFrame }) => capturedFrame),
    [capturedFrames],
  );
  const visibleCapturedFrameList = useMemo(
    () => visibleCapturedFrames.map(({ frame: capturedFrame }) => capturedFrame),
    [visibleCapturedFrames],
  );
  const visibleTargetIdList = useMemo(() => [...visibleTargetIds], [visibleTargetIds]);
  // 푸터 capture 버튼과 동일한 비활성 조건 — placeholder의 안내용 capture 액션에 사용.
  const placeholderCaptureDisabled =
    !timelineContextActive ||
    !(activeMediaResult?.source.baseMedia.src || selectedMedia) ||
    snapshot.operations.capture.pending ||
    currentCameraSavePending ||
    !authoringOperationsAvailable;
  // The empty Inspector follows the same source of truth as the timeline actions. Panel selection
  // may intentionally remain on a prior frame while the playhead moves elsewhere.
  const placeholderEditFrameId =
    timelineContextActive && frameAtPlayhead ? frameAtPlayhead.frameId : null;
  const trackingControl = useMemo(
    () =>
      snapshot.trackingAvailable && trackingControlVisible ? (
        <TrackingPreviewControls
          status={trackingStatusForCurrentContext}
          framesSeen={trackingContextActive ? snapshot.trackingPreview.framesSeen : 0}
          errorMessage={
            trackingContextActive
              ? resolveAnnotationOperationErrorMessage(snapshot.trackingPreview.error, locale)
              : undefined
          }
          locale={locale}
          onStart={handleStartSelectedTrackingPreview}
          onStop={handleStopTrackingPreview}
        />
      ) : undefined,
    [
      handleStartSelectedTrackingPreview,
      handleStopTrackingPreview,
      locale,
      snapshot.trackingAvailable,
      snapshot.trackingPreview.error,
      snapshot.trackingPreview.framesSeen,
      trackingContextActive,
      trackingControlVisible,
      trackingStatusForCurrentContext,
    ],
  );
  // The generic product action owns this seat once supplied. Legacy tracking remains only as an
  // omitted-prop fallback during the migration window, never as a second competing action.
  const timelineTrailingAction = timelineAction === undefined ? trackingControl : timelineAction;
  const previewLayout = useCapturedFramePreviewLayout(
    toolsPresentation.viewportRef,
    displayedCapturedFrames.length,
    `${annotationContextActive}:${toolsPresentation.compact}`,
    materializedPreviewFrame !== undefined,
    annotationContextActive ? toolsPresentation.compactHeight : 0,
    toolsPresentation.setPreviewCompact,
    toolsPresentation.allowPreviewCompaction,
  );
  const previewSelection = useCapturedFramePreviewSelection({
    previewFrameId,
    frame: materializedPreviewFrame,
    targets: workspace.targets,
  });
  const capturedFramePreviewContent = useMemo(
    () =>
      materializedPreviewFrame ? (
        <CapturedFramePreviewPanel
          locale={locale}
          viewMode="browse"
          frame={materializedPreviewFrame}
          outsideFilter={previewOutsideFrameFilter}
          media={previewFrameMedia}
          sequences={railSequences}
          streams={workspace.streams}
          targets={workspace.targets}
          targetNames={currentTargetPresentation.targetNames}
          onEdit={
            authoring ? () => handleEditCapturedFrame(materializedPreviewFrame.frameId) : undefined
          }
          onClose={handleCloseCapturedFramePreview}
          popup={!previewLayout.inline}
          canDock={previewLayout.canDock}
          dockRequiresCompact={previewLayout.dockRequiresCompact}
          onPlacementChange={previewLayout.inline ? previewLayout.detach : previewLayout.dock}
          selection={previewSelection}
        />
      ) : undefined,
    [
      previewLayout.inline,
      previewLayout.canDock,
      previewLayout.dockRequiresCompact,
      previewLayout.detach,
      previewLayout.dock,
      previewSelection,
      authoring,
      currentTargetPresentation.targetNames,
      handleEditCapturedFrame,
      locale,
      materializedPreviewFrame,
      previewOutsideFrameFilter,
      previewFrameMedia,
      handleCloseCapturedFramePreview,
      railSequences,
      workspace.streams,
      workspace.targets,
    ],
  );
  return (
    <StudioRoot
      ref={studioRootRef}
      $leadSection={panelLeadVisible}
      className={className}
      role="region"
      aria-label={annotationStudioText("title", locale)}
      aria-keyshortcuts={studioAriaKeyShortcuts}
    >
      <StudioCommandBar
        locale={locale}
        viewMode={studioViewMode}
        frame={frame}
        targets={workspace.targets}
        viewportContextLabel={viewportContextLabel}
        frameEditingStatusState={frameEditingStatusState}
        validationState={annotationSetValidation}
        validationSummaryLocation={annotationValidationSummaryLocation}
        validationIssueLocations={annotationValidationIssueLocations}
        validationReviewDestination={annotationValidationReviewDestination}
        validationPopupOpenRequestId={validationPopupOpenRequestId}
        materializingFrameId={materializingFrameId}
        hasUnsavedAnnotationChanges={hasUnsavedAnnotationChanges}
        hasAnnotationContent={hasAnnotationContent}
        exitActionState={exitActionState}
        exitAvailable={Boolean(onExit) && exitRequestId === undefined}
        exitDisabled={
          exitActionState !== "idle" ||
          currentCameraSavePending ||
          segmentationQueuedOrPending ||
          snapshot.operations.capture.pending ||
          snapshot.operations.save.pending ||
          removingCapturedFrame
        }
        onValidate={handleValidate}
        onOpenValidationIssue={handleOpenValidationIssue}
        onOpenValidationReview={handleOpenValidationReview}
        onExit={handleExit}
      />
      <StudioWorkspace>
        <StudioContextRail
          contextLead={contextLead}
          locale={locale}
          labels={snapshot.labels}
          sequences={railSequences}
          sequenceDirectory={sequenceDirectory}
          pinnedSequenceId={snapshot.pinnedSequence?.id ?? null}
          onSequenceDirectoryQueryChange={controller.setSequenceDirectoryQuery}
          onLoadNextSequencePage={controller.loadNextSequencePage}
          onRetrySequenceDirectory={controller.retrySequenceDirectory}
          newSequenceIds={newSequenceIdSet}
          globalStreams={globalStreams}
          selectedSequenceId={snapshot.selectedSequenceId}
          selectedStreamId={snapshot.selectedStreamId}
          dirtyFrameCountsByStream={dirtyFrameCountsByStream}
          dirtyFrameCountsBySequenceAndStream={dirtyFrameCountsBySequenceAndStream}
          saveConflictFrameCountsByStream={saveConflictFrameCountsByStream}
          saveConflictFrameCountsBySequenceAndStream={saveConflictFrameCountsBySequenceAndStream}
          validationIssueFrameCountsByStream={validationIssueFrameCountsByStream}
          validationIssueFrameCountsBySequenceAndStream={
            validationIssueFrameCountsBySequenceAndStream
          }
          capturedFrameCountsByStream={capturedFrameCountsByStream}
          savedSequenceContentCountsByStream={savedSequenceContentCountsByStream}
          unannotatedFrameCountsBySequenceAndStream={unannotatedFrameCountsBySequenceAndStream}
          annotatedFrameCountsBySequenceAndStream={annotatedFrameCountsBySequenceAndStream}
          trackableSequenceIds={trackableSequenceIds}
          trackableStreamIds={trackableStreamIds}
          runnableTrackingSequenceIds={runnableTrackingSequenceIds}
          trackingStartBlocked={trackingStartBlocked}
          trackingPreviewTargetMedia={snapshot.trackingPreview.targetMedia}
          trackingPreviewStatus={snapshot.trackingPreview.status}
          trackingPreviewErrorMessage={resolveAnnotationOperationErrorMessage(
            snapshot.trackingPreview.error,
            locale,
          )}
          renderSequenceAction={sequenceAction}
          onSelectSequence={handleSelectSequence}
          onSelectStream={handleSelectStream}
          onOpenSequenceInVideo={handleOpenSequenceInVideo}
          onOpenStreamInVideo={handleOpenStreamInVideo}
          onStartTrackingPreview={handleStartEpisodeTrackingPreview}
          onStopTrackingPreview={handleStopTrackingPreview}
        />

        <Main $leadSection={panelLeadVisible}>
          <WorkspaceBody
            $showAnnotationPanel={annotationPanelVisible}
            $leadSection={panelLeadVisible}
          >
            <MediaWorkspace>
              <StudioViewport
                controller={controller}
                locale={locale}
                mediaContext={mediaContext}
                browseMediaKey={JSON.stringify([
                  snapshot.selectedSequenceId,
                  snapshot.selectedStreamId,
                  selectedMedia?.src,
                  selectedMedia?.crossOrigin,
                ])}
                stream={stream}
                media={selectedMedia}
                capturedFrameMedia={capturedFrameMedia}
                trackingStream={trackingTargetStream}
                trackingMedia={trackingTargetMediaSource}
                frame={frame}
                capturedFrameResource={capturedFrameResource}
                mediaResult={activeMediaResult}
                capturedFrameEditable={authoring}
                targets={workspace.targets}
                frameTargets={frameTargets}
                browseTransport={playbackTransport}
                trackingPreview={snapshot.trackingPreview}
                trackingPlayback={trackingPreviewPlayback}
                coordinateGuideVisible={coordinateOverlay.guideVisible}
                coordinatePreview={coordinateOverlay.preview}
                onTimelineDurationChange={handleTimelineDurationChange}
                onTimelineCurrentTimeChange={handleTimelineCurrentTimeChange}
                onTimelineStatusChange={handleTimelineStatusChange}
                onTimelineEnded={handleTimelineEnded}
                onPoint={handleAddPoint}
                onRemovePoint={handleRemovePoint}
              />
              <StudioMediaControls
                locale={locale}
                playbackIdentity={
                  activeMediaResult
                    ? `result:${activeMediaResult.source.id}:${String(activeMediaResult.source.revision)}`
                    : `${snapshot.selectedSequenceId ?? "none"}:${snapshot.selectedStreamId ?? "none"}`
                }
                viewMode={studioViewMode}
                playbackTransport={transport}
                onPrimaryPlaybackToggle={
                  trackingTimelineActive ? handlePrimaryPlaybackToggle : undefined
                }
                streamAvailable={Boolean(activeMediaResult?.source.baseMedia.src || selectedMedia)}
                timelineContextActive={timelineContextActive}
                resultPlaybackActive={activeMediaResult !== null || trackingTimelineActive}
                markers={playbackTimelineMarkers}
                timelineAction={timelineTrailingAction}
                authoring={authoring}
                frameAtPlayheadId={frameAtPlayhead?.frameId ?? null}
                previewFrameId={previewFrameId}
                capturePending={snapshot.operations.capture.pending}
                captureDisabled={currentCameraSavePending || !authoringOperationsAvailable}
                authoringDisabledReasonId={
                  authoringAvailabilityReason ? authoringAvailabilityReasonId : undefined
                }
                removeAvailable={Boolean(controller.removeCapturedFrame)}
                removeDisabled={capturedFrameRemovalDisabled}
                captureButtonRef={frameCaptureButtonRef}
                onMarkerSelect={handleMarkerSelect}
                onToggleCapturedFramePreview={handleToggleCapturedFramePreview}
                onEditCapturedFrame={handleEditCapturedFrame}
                onBrowseVideoRequest={handleBrowseVideoRequest}
                onCapture={handleCapture}
                onRequestRemove={requestCapturedFrameRemoval}
              />
            </MediaWorkspace>

            <AnnotationPanel
              $visible={annotationPanelVisible}
              hidden={!annotationPanelVisible}
              aria-label={annotationStudioText(
                studioViewMode === "annotate" ? "annotationStatus" : "capturedFrame",
                locale,
              )}
            >
              <AnnotationPanelHeaderContent
                compact={toolsPresentation.compact}
                onCompactChange={toolsPresentation.setCompact}
                toolsContentId={toolsPresentation.contentId}
                locale={locale}
                toolsVisible={annotationToolsVisible}
                authoring={authoring}
                annotationActions={
                  authoring && annotationContextActive && frame ? (
                    <AnnotationPointActions
                      locale={locale}
                      interactionDisabled={!annotationToolsVisible}
                      selectedTargetId={snapshot.selectedTargetId}
                      selectedTargetColor={selectedTargetColor}
                      canUndo={snapshot.canUndo}
                      canRedo={snapshot.canRedo}
                      onCoordinateOverlayChange={setCoordinateOverlay}
                      onAddPoint={handleAddPoint}
                      onUndo={controller.undo}
                      onRedo={controller.redo}
                    />
                  ) : undefined
                }
              />

              <AnnotationPanelBody data-annotation-panel-body="">
                {panelLeadVisible ? (
                  <AnnotationPanelLeadSection data-annotation-panel-lead-section="">
                    {panelLead}
                  </AnnotationPanelLeadSection>
                ) : null}
                {(() => {
                  const capturedFrameSection = (
                    <CapturedFramePanel
                      locale={locale}
                      viewMode={studioViewMode}
                      streamLabel={capturedFrameCameraLabel}
                      streamFieldLabel={snapshot.labels.stream[locale]}
                      sequences={railSequences}
                      streams={workspace.streams}
                      targets={workspace.targets}
                      capturedFrames={capturedFrameList}
                      visibleFrames={visibleCapturedFrameList}
                      displayedFrameCount={displayedCapturedFrames.length}
                      frameScope={effectiveCapturedFrameScope}
                      onFrameScopeChange={setCapturedFrameScope}
                      selectedSequenceId={snapshot.selectedSequenceId}
                      selectedSequenceLabel={sequence?.label ?? snapshot.selectedSequenceId ?? "—"}
                      activeFrameId={frame?.frameId ?? null}
                      selectedFrameId={selectedCapturedFrameId}
                      previewFrameId={previewFrameId}
                      previewFrame={previewFrame}
                      previewOutsideFilter={previewOutsideFrameFilter}
                      materializingFrameId={materializingFrameId}
                      materializationFailedFrameId={materializationFailure?.frameId ?? null}
                      savedAnnotationFrameIds={savedAnnotationFrameIds}
                      dirtyFrameIds={dirtyFrameIds}
                      saveConflictFrameIds={saveConflictFrameIds}
                      validationIssueFrameIds={validationIssueFrameIds}
                      page={capturedFramePage}
                      pageCount={capturedFramePageCount}
                      listViewportRef={capturedFrameListViewportRef}
                      listRef={capturedFrameListRef}
                      authoring={authoring}
                      hasUnsavedCameraChanges={currentCameraDirtyFrameIds.length > 0}
                      cameraSavePending={currentCameraSavePending}
                      cameraSaveDisabled={
                        currentCameraDirtyFrameIds.length === 0 ||
                        !authoringOperationsAvailable ||
                        currentCameraSavePending ||
                        cameraChangeSaving ||
                        exitActionState !== "idle" ||
                        segmentationQueuedOrPending ||
                        snapshot.operations.capture.pending ||
                        snapshot.operations.save.pending ||
                        removingCapturedFrame
                      }
                      authoringDisabledReasonId={
                        authoringAvailabilityReason ? authoringAvailabilityReasonId : undefined
                      }
                      removeAvailable={Boolean(controller.removeCapturedFrame)}
                      removeDisabled={capturedFrameRemovalDisabled}
                      onSaveCameraAnnotations={handleSaveCameraAnnotations}
                      onSelectFrame={handleSelectFrameForActions}
                      onTogglePreview={handleToggleCapturedFramePreview}
                      onRequestRemove={requestCapturedFrameRemoval}
                      onPreviousPage={handlePreviousCapturedFramePage}
                      onNextPage={handleNextCapturedFramePage}
                    />
                  );

                  const annotationControls =
                    annotationContextActive && frame ? (
                      <AnnotationToolsPanel
                        compact={toolsPresentation.compact}
                        contentId={toolsPresentation.contentId}
                        contentRef={toolsPresentation.contentRef}
                        locale={locale}
                        authoring={authoring}
                        interactionDisabled={!annotationToolsVisible}
                        frame={frame}
                        targets={workspace.targets}
                        selectedTargetId={snapshot.selectedTargetId}
                        targetNames={currentTargetPresentation.targetNames}
                        selectedOnly={currentTargetPresentation.selectedOnly}
                        visibleTargetIds={visibleTargetIdList}
                        dirtyTargetIds={dirtyTargetIds}
                        pendingTargetIds={pendingSegmentationTargetIds}
                        discardDisabled={
                          !activeFrameHasUnsavedAnnotationChanges ||
                          currentCameraSavePending ||
                          snapshot.operations.capture.pending ||
                          snapshot.operations.save.pending ||
                          removingCapturedFrame
                        }
                        saveDisabled={
                          !activeFrameHasUnsavedAnnotationChanges ||
                          !authoringOperationsAvailable ||
                          currentCameraSavePending ||
                          segmentationQueuedOrPending ||
                          snapshot.operations.save.pending ||
                          removingCapturedFrame
                        }
                        authoringDisabledReasonId={
                          authoringAvailabilityReason ? authoringAvailabilityReasonId : undefined
                        }
                        savePending={snapshot.operations.save.pending}
                        onSelectedOnlyChange={handleSelectedOnlyChange}
                        onVisibleTargetIdsChange={handleVisibleTargetIdsChange}
                        onTargetChange={handleTargetChange}
                        onClearTarget={controller.clearTarget}
                        onRequestClearAllTargets={handleRequestClearAllTargets}
                        onTargetNameChange={handleTargetNameChange}
                        onRequestDiscardChanges={handleRequestDiscardChanges}
                        onSave={handleSaveActiveFrame}
                      />
                    ) : null;
                  return (
                    <>
                      <AnnotationInspectorRoot
                        data-annotation-inspector={annotationContextActive ? "active" : "inactive"}
                      >
                        <AnnotationInspectorBody
                          ref={toolsPresentation.viewportRef}
                          data-annotation-inspector-body=""
                        >
                          {annotationControls ? (
                            <AnnotationInspectorTools
                              disabled={!annotationToolsVisible}
                              aria-disabled={!annotationToolsVisible}
                              aria-label={annotationStudioText("annotationStatus", locale)}
                              data-annotation-inspector-tools=""
                            >
                              {annotationControls}
                            </AnnotationInspectorTools>
                          ) : (
                            // 빈 inspector는 playhead의 다음 action을 같은 위치에 유지한다: 현재 시점에
                            // frame이 없으면 capture, 있으면 그 frame의 Annotation 편집.
                            <AnnotationControlsPlaceholder
                              action={
                                authoring &&
                                timelineContextActive &&
                                placeholderEditFrameId === null ? (
                                  <IconButton
                                    data-annotation-placeholder-capture=""
                                    size="md"
                                    variant="solid"
                                    text={annotationStudioText("captureFrame", locale)}
                                    aria-describedby={
                                      placeholderCaptureDisabled && authoringAvailabilityReason
                                        ? authoringAvailabilityReasonId
                                        : undefined
                                    }
                                    disabled={placeholderCaptureDisabled}
                                    onClick={() => handleCapture()}
                                  >
                                    {snapshot.operations.capture.pending ? (
                                      <Spinner
                                        label={annotationStudioText("capturingFrame", locale)}
                                        size="sm"
                                      />
                                    ) : (
                                      <CameraIcon />
                                    )}
                                  </IconButton>
                                ) : authoring && placeholderEditFrameId !== null ? (
                                  <IconButton
                                    data-annotation-placeholder-edit=""
                                    size="md"
                                    variant="solid"
                                    text={annotationStudioText("annotationEditMode", locale)}
                                    aria-busy={
                                      materializingFrameId === placeholderEditFrameId || undefined
                                    }
                                    aria-describedby={
                                      !authoringOperationsAvailable && authoringAvailabilityReason
                                        ? authoringAvailabilityReasonId
                                        : undefined
                                    }
                                    disabled={
                                      !authoringOperationsAvailable ||
                                      currentCameraSavePending ||
                                      materializingFrameId === placeholderEditFrameId
                                    }
                                    onClick={() => handleEditCapturedFrame(placeholderEditFrameId)}
                                  >
                                    {materializingFrameId === placeholderEditFrameId ? (
                                      <Spinner
                                        label={annotationStudioText("loadingCapturedFrame", locale)}
                                        size="sm"
                                      />
                                    ) : (
                                      <AnnotationIcon aria-hidden="true" />
                                    )}
                                  </IconButton>
                                ) : undefined
                              }
                            >
                              {annotationStudioText(
                                placeholderEditFrameId !== null
                                  ? "annotationEditAvailable"
                                  : authoring
                                    ? "emptyCapturedFrames"
                                    : "noCapturedFramesForContext",
                                locale,
                              )}
                            </AnnotationControlsPlaceholder>
                          )}
                        </AnnotationInspectorBody>
                        {previewLayout.inline && capturedFramePreviewContent ? (
                          <InlineFramePreview
                            data-preview-layout="inline"
                            data-annotation-reference-preview=""
                            onKeyDown={(event) => {
                              if (event.key === "Escape") {
                                event.stopPropagation();
                                handleCloseCapturedFramePreview();
                              }
                            }}
                          >
                            {capturedFramePreviewContent}
                          </InlineFramePreview>
                        ) : null}
                        {toolsOverlayVisible ? (
                          <AnnotationToolsOverlay data-annotation-tools-overlay="">
                            {toolsOverlay}
                          </AnnotationToolsOverlay>
                        ) : null}
                      </AnnotationInspectorRoot>
                      {capturedFrameSection}
                    </>
                  );
                })()}
              </AnnotationPanelBody>
            </AnnotationPanel>
          </WorkspaceBody>

          <StudioAuthoringAvailabilityMessage
            id={authoringAvailabilityReasonId}
            message={authoringAvailabilityReason}
          />
        </Main>
      </StudioWorkspace>
      {annotationSetValidation ? (
        <AnnotationValidationReviewModal
          open={exitValidationReviewRequested || pendingValidationReviewRequest !== null}
          mode={pendingValidationReviewRequest ? "navigation" : "exit"}
          state={annotationSetValidation}
          locale={locale}
          targets={workspace.targets}
          summaryLocation={annotationValidationSummaryLocation}
          issueLocations={annotationValidationIssueLocations}
          currentFrameId={frame?.frameId ?? null}
          materializingFrameId={materializingFrameId}
          hasUnsavedAnnotationChanges={hasUnsavedAnnotationChanges}
          hasAnnotationContent={hasAnnotationContent}
          overridePending={pendingValidationReviewRequest === null && exitActionState !== "idle"}
          onOpenIssue={handleOpenValidationIssue}
          onOpenSummary={
            annotationValidationReviewDestination
              ? () =>
                  handleOpenValidationReview(
                    annotationValidationReviewDestination.frameId,
                    annotationValidationReviewDestination.targetId,
                  )
              : null
          }
          onCancel={
            pendingValidationReviewRequest ? cancelValidationReview : handleCancelExitReview
          }
          onContinueAnyway={
            pendingValidationReviewRequest ? approveValidationReviewOverride : handleExitAnyway
          }
        />
      ) : null}
      <StudioDialogs
        locale={locale}
        clearAllOpen={clearAllConfirmationOpen}
        removeFrameOpen={frameRemovalConfirmationId !== null}
        removingFrame={removingCapturedFrame}
        discardChangesOpen={discardChangesConfirmationOpen}
        draftHasSavedBase={Boolean(snapshot.draft?.base)}
        cameraChangeOpen={pendingCameraChange !== null}
        cameraChangeSaving={cameraChangeSaving}
        cameraChangeError={cameraChangeError}
        discardAndChangeCameraDisabled={
          cameraChangeSaving ||
          currentCameraSavePending ||
          snapshot.operations.capture.pending ||
          snapshot.operations.save.pending ||
          removingCapturedFrame
        }
        saveAndChangeCameraDisabled={
          !authoringOperationsAvailable ||
          cameraChangeSaving ||
          currentCameraSavePending ||
          segmentationQueuedOrPending ||
          snapshot.operations.capture.pending ||
          snapshot.operations.save.pending ||
          removingCapturedFrame
        }
        leaveConfirmationOpen={snapshot.leaveConfirmation !== null}
        onCloseClearAll={() => setClearAllConfirmationOpen(false)}
        onConfirmClearAll={() => {
          controller.clearAllTargets();
          setClearAllConfirmationOpen(false);
        }}
        onCloseRemoveFrame={() => {
          if (!removingCapturedFrame) setFrameRemovalConfirmationId(null);
        }}
        onConfirmRemoveFrame={() => {
          if (frameRemovalConfirmationId !== null) {
            const removal = controller.removeCapturedFrame?.(frameRemovalConfirmationId);
            if (removal) {
              void removal.then(() => setFrameRemovalConfirmationId(null));
              return;
            }
          }
          setFrameRemovalConfirmationId(null);
        }}
        onCloseDiscardChanges={() => setDiscardChangesConfirmationOpen(false)}
        onConfirmDiscardChanges={() => {
          if (frame) controller.discardFrameChanges(frame.frameId);
          setDiscardChangesConfirmationOpen(false);
        }}
        onCloseCameraChange={closeCameraChange}
        onDiscardAndChangeCamera={discardAndChangeCamera}
        onSaveAndChangeCamera={() => void saveAndChangeCamera()}
        onCancelLeave={controller.cancelLeave}
        onConfirmLeave={controller.confirmLeave}
      />
      <CapturedFramePreviewPopup
        frameId={previewLayout.inline ? null : (materializedPreviewFrame?.frameId ?? null)}
        label={annotationStudioText("framePreview", locale)}
        studioRootRef={studioRootRef}
        listRef={capturedFrameListRef}
        listViewportRef={capturedFrameListViewportRef}
        rows={visibleCapturedFrameList}
        onClose={handleCloseCapturedFramePreview}
      >
        {capturedFramePreviewContent}
      </CapturedFramePreviewPopup>
      <ToastHost manager={studioToastManager} />
    </StudioRoot>
  );
}
